// ------------------------------------------------------------------------ //
// TanStack Query hooks for Activities
// ------------------------------------------------------------------------ //
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@shared/api/api';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import Auth from '@shared/services/Auth';
import { getUserTimezone } from '@shared/utils/timezone';

// Extend dayjs with timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);

// ------------------------------------------------------------------------ //
// Query Keys
// ------------------------------------------------------------------------ //
export const activityKeys = {
  all: ['activities'],
  today: () => [...activityKeys.all, 'today'],
  inProgress: () => [...activityKeys.all, 'inProgress'],
  running: () => [...activityKeys.all, 'running'],
  directHoursStatus: () => ['directHoursStatus'],
};

// ------------------------------------------------------------------------ //
// Get Today's Activities (auto-refreshes every 10 seconds)
// ------------------------------------------------------------------------ //
export function useTodayActivities() {
  const { user } = Auth.user() || { user: {} };
  const userTimezone = getUserTimezone();

  return useQuery({
    queryKey: activityKeys.today(),
    queryFn: async () => {
      const params = {
        userId: user.id,
        startedAt: dayjs().tz(userTimezone).hour(0).minute(0).second(0).unix(),
        endedAt: dayjs().tz(userTimezone).hour(23).minute(59).second(59).unix(),
      };
      return api.getActivitiesUser(params);
    },
    enabled: !!user.id,
    refetchInterval: 10000,
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

// ------------------------------------------------------------------------ //
// Get In-Progress Activity
// ------------------------------------------------------------------------ //
export function useInProgressActivity() {
  return useQuery({
    queryKey: activityKeys.inProgress(),
    queryFn: async () => {
      const response = await api.getInProgressActivity();
      return response.id ? response : null;
    },
    refetchInterval: 10000,
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

// ------------------------------------------------------------------------ //
// Get Running Activities (for all users)
// ------------------------------------------------------------------------ //
export function useRunningActivities() {
  return useQuery({
    queryKey: activityKeys.running(),
    queryFn: api.getUsersRunningActivities,
    refetchInterval: 10000,
    staleTime: 5000,
  });
}

// ------------------------------------------------------------------------ //
// Get Direct Hours Status (polls every 30s)
// ------------------------------------------------------------------------ //
export function useDirectHoursStatus() {
  return useQuery({
    queryKey: activityKeys.directHoursStatus(),
    queryFn: api.getSlotStatus,
    refetchInterval: 30000,
    staleTime: 0,
    select: (data) => ({
      isWithinDirectHours: data.is_within_direct_hours,
      closingAt: data.closing_at,
    }),
  });
}

// ------------------------------------------------------------------------ //
// Start Activity Mutation
// ------------------------------------------------------------------------ //
export function useStartActivity() {
  const queryClient = useQueryClient();
  const { user } = Auth.user() || { user: {} };

  return useMutation({
    mutationFn: async ({ typeId, groupId, specialName, timezoneId, isReinforcment, isGuest }) => {
      // Ne pas envoyer startedAt - l'API utilise now() côté serveur avec le bon fuseau horaire
      const params = {
        manual: false,
        activityTypeId: typeId,
        activityGroupId: groupId,
        specialActivityName: specialName,
        timezoneId: timezoneId,
        reinforcement: isReinforcment,
        guest: isGuest,
      };

      await api.postActivity(params);

      // Update user status
      await api.putUserInfo({
        userId: user.id,
        statusId: 3,
      });

      // Get the new in-progress activity
      return api.getInProgressActivity();
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries(activityKeys.inProgress());
      queryClient.invalidateQueries(activityKeys.today());
    },
  });
}

// ------------------------------------------------------------------------ //
// Stop Activity Mutation
// ------------------------------------------------------------------------ //
export function useStopActivity() {
  const queryClient = useQueryClient();
  const { user } = Auth.user() || { user: {} };

  return useMutation({
    mutationFn: async (activityId) => {
      // Utiliser le nouvel endpoint dédié pour arrêter l'activité
      // Le serveur utilise Carbon::now() pour l'heure de fin
      const response = await api.stopActivity(activityId);

      // Update user status
      await api.putUserInfo({
        userId: user.id,
        statusId: 1,
      });

      return response;
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries(activityKeys.inProgress());
      queryClient.invalidateQueries(activityKeys.today());
    },
  });
}
